import { useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { ReasonDialog } from "../../components/dash/ReasonDialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { STATE_COLORS } from "../../components/map/LandMap";
import { formatRupiah, formatDay } from "../../lib/labels";
import { n } from "./shared";
import { today } from "../profit/shared";

export const AreaStatePill = ({ state, testid }) => {
  const [color, label] = STATE_COLORS[state] || STATE_COLORS.INACTIVE;
  return <span className="inline-flex w-fit items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-bold text-white whitespace-nowrap" style={{ background: color }} data-testid={testid}>{label.split(" · ")[1]}</span>;
};

export const CapacitySummary = ({ land, testid = "capacity-summary" }) => (
  <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3 text-sm" data-testid={testid}>
    {[["Luas total", `${n(land.areaHectares)} ha`], ["Tidak ditanami", `${n(land.excludedHectares)} ha`], ["Luas efektif", `${n(land.effectiveAreaHectares)} ha`],
      ["Kapasitas", `${n(land.capacity)} btg`], ["Terinvestasi", `${n(land.investedTrees)} btg`, "#2563eb"], ["Dibooking", `${n(land.bookedTrees)} btg`, "#ca8a04"],
      ["Tersedia", `${n(land.availableTrees)} btg`, "#16a34a"]].map(([k, v, c]) => (
      <div key={k} className="rounded-lg border border-slate-200 p-2.5"><p className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold">{k}</p><p className="font-bold" style={{ color: c }} data-testid={`${testid}-${k.toLowerCase().replace(/\s/g, "-")}`}>{v}</p></div>
    ))}
  </div>
);

const ExtendDialog = ({ booking, open, onOpenChange, reload }) => {
  const [days, setDays] = useState(7);
  const [reason, setReason] = useState("");
  const save = async () => {
    try { await api.post(`/bookings/${booking.id}/extend`, { days: Number(days), reason }); toast.success("Booking diperpanjang"); onOpenChange(false); reload(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="extend-dialog">
        <DialogHeader><DialogTitle>Perpanjang booking</DialogTitle></DialogHeader>
        <Input type="number" min="1" max="30" value={days} onChange={(e) => setDays(e.target.value)} data-testid="extend-days" />
        <Input placeholder="Alasan perpanjangan (min. 5 karakter)" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="extend-reason" />
        <Button disabled={reason.length < 5} onClick={save} className="bg-emerald-800 hover:bg-emerald-900" data-testid="extend-save">Perpanjang</Button>
      </DialogContent>
    </Dialog>
  );
};

export const ProjectRow = ({ p, bookings, reload }) => {
  const { user } = useAuth();
  const farmer = user.role === "FARMER_GROUP";
  const [dlg, setDlg] = useState(null);
  const live = bookings.find((b) => b.projectId === p.id && ["ACTIVE", "CONTRACTED"].includes(b.status));
  const contractActive = p.contract?.status === "LOCKED";
  const ended = contractActive && p.contract.endDate && p.contract.endDate <= today();
  const meta = [p.investmentValue && `Kebutuhan investasi ${formatRupiah(p.investmentValue)}`, p.areaHectares && `${n(p.areaHectares)} ha`,
    live && `Booking oleh ${live.investorName || "-"} · ${live.status === "ACTIVE" ? `berakhir ${formatDay(live.expiresAt)}` : "kontrak efektif"}`,
    contractActive && !p.fundingConfirmedAt && "Kontrak efektif, menunggu konfirmasi pendanaan 100%",
    p.handedOverAt && `Diserahterimakan ${formatDay(p.handedOverAt)}`, contractActive && "Area & jumlah batang dikunci"].filter(Boolean).join(" · ");
  const post = async (url, body, msg) => { try { await api.post(url, body); toast.success(msg); reload(); } catch (e) { toast.error(formatApiError(e)); return false; } };
  return (
    <div className="py-3 space-y-2" data-testid={`land-project-${p.id}`}>
      <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
        <span><b className="font-mono">{p.projectCode}</b> · {p.name}</span>
        <span className="flex items-center gap-2"><b>{n(p.targetTrees)} batang</b><AreaStatePill state={p.areaState} testid={`project-state-${p.id}`} /></span>
      </div>
      <p className="text-xs text-slate-600">{meta}</p>
      {farmer && (
        <div className="flex flex-wrap gap-2">
          {live?.status === "ACTIVE" && <Button size="sm" variant="outline" onClick={() => setDlg("extend")} data-testid={`extend-booking-${p.id}`}>Perpanjang Booking</Button>}
          {live?.status === "ACTIVE" && <Button size="sm" variant="outline" className="text-red-700" onClick={() => setDlg("cancel")} data-testid={`cancel-booking-${p.id}`}>Batalkan Booking</Button>}
          {contractActive && !p.fundingConfirmedAt && !ended && <Button size="sm" className="bg-blue-700 hover:bg-blue-800" onClick={() => setDlg("fund")} data-testid={`confirm-funding-${p.id}`}>Konfirmasi Pendanaan 100%</Button>}
          {ended && !p.handedOverAt && <Button size="sm" variant="outline" onClick={() => setDlg("handover")} data-testid={`handover-${p.id}`}>Catat Serah Terima Kebun</Button>}
        </div>
      )}
      {live && <ExtendDialog booking={live} open={dlg === "extend"} onOpenChange={(o) => !o && setDlg(null)} reload={reload} />}
      <ReasonDialog open={["cancel", "fund", "handover"].includes(dlg)} onOpenChange={(o) => !o && setDlg(null)} danger={dlg === "cancel"} confirmLabel="Konfirmasi"
        title={{ cancel: "Batalkan booking", fund: "Konfirmasi dana 100% diterima sesuai kontrak", handover: "Serah terima kebun ke Kelompok Tani/Koperasi & pemilik lahan" }[dlg] || ""}
        onConfirm={(reason) => (dlg === "cancel" ? post(`/bookings/${live.id}/cancel`, { reason }, "Booking dibatalkan, area kembali tersedia")
          : dlg === "fund" ? post(`/projects/${p.id}/confirm-funding`, { reason }, "Status menjadi TERINVESTASI")
            : post(`/projects/${p.id}/handover`, { reason, handoverDate: today() }, "Status menjadi KONTRAK SELESAI"))} />
    </div>
  );
};
